"use client";

import { useCallback, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronDown, X } from "lucide-react";
import { MobileMenuList } from "./MobileMenu";

interface INavbarProps {
  menu: {
    NATIONAL_TOURS: Record<string, { name: string; url: string }[]>;
    INTERNATIONAL_TOURS: Record<string, { name: string; url: string }[]>;
  };
}

const MENU_TITLES: Record<keyof INavbarProps["menu"], string> = {
  NATIONAL_TOURS: "National Tours",
  INTERNATIONAL_TOURS: "International Tours",
};

export default function Navbar({ menu }: INavbarProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  const isActive = useCallback((href: string) => pathname === href, [pathname]);

  const toggleMenu = useCallback(() => setIsOpen((prev) => !prev), []);
  const closeMenu = useCallback(() => setIsOpen(false), []);

  const renderNavLink = (href: string, label: string, active: boolean) => (
    <Link
      href={href}
      className={`relative inline-flex items-center gap-1 ${
        active ? "text-primary-500 font-medium" : "hover:text-primary-600 group"
      }`}
    >
      {label}
      <span
        className={`absolute left-0 -bottom-0 h-[2px] bg-primary-500 transition-all duration-500 origin-left ${
          active ? "w-full" : "w-0 group-hover:w-full"
        }`}
      />
    </Link>
  );

  return (
    <nav className="relative bg-white z-50">
      {/* DESKTOP MENU */}
      <ul className="hidden md:flex items-center gap-6 px-6 py-4 text-grey-900">
        <li>{renderNavLink("/", "Home", isActive("/"))}</li>

        {(Object.keys(menu) as (keyof typeof menu)[]).map((key) => {
          const hasItems = Object.values(menu[key]).some(
            (list) => list.length > 0
          );

          return (
            <li
              key={key}
              className={`relative ${hasItems ? "group cursor-pointer hover:text-primary-500" : ""}`}
            >
              <span className="relative inline-flex items-center gap-1">
                {MENU_TITLES[key]}
                {hasItems && (
                  <ChevronDown className="w-4 h-4 transition-transform duration-500 group-hover:rotate-180" />
                )}
                <span
                  className={`absolute left-0 -bottom-0 h-[2px] bg-primary-500 ${
                    hasItems
                      ? "w-0 group-hover:w-full transition-all duration-500 origin-left"
                      : "w-0"
                  }`}
                />
              </span>

              {hasItems && (
                <div className="absolute left-0 top-full hidden group-hover:block -translate-x-1/5 bg-white p-8 rounded min-w-[900px] z-50 max-h-96 overflow-y-auto custom-scrollbar shadow-xl">
                  {Object.entries(menu[key]).map(([category, list]) =>
                    list.length ? (
                      <div
                        key={category}
                        className="inline-block w-1/4 align-top mb-4 px-2"
                      >
                        <p className="font-medium text-black mb-2">
                          {category}
                        </p>
                        <ul className="flex flex-col gap-4">
                          {list.map((item) => (
                            <li key={item?.url}>
                              <Link
                                href={item.url}
                                className="text-grey-700 hover:text-black typography-large-body font-light"
                              >
                                {item?.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null
                  )}
                </div>
              )}
            </li>
          );
        })}

        <li>{renderNavLink("/package", "Packages", isActive("/package"))}</li>

        <li>{renderNavLink("/about-us", "About Us", isActive("/about-us"))}</li>

        <li>{renderNavLink("/blog", "Blog", isActive("/blog"))}</li>

        <li>
          {renderNavLink("/contact-us", "Contact Us", isActive("/contact-us"))}
        </li>
      </ul>

      {/* MOBILE MENU BUTTON */}
      <button
        onClick={toggleMenu}
        className="md:hidden flex h-8 w-8 flex-col items-center justify-center space-y-1 px-2"
      >
        <span
          className={`block h-0.5 w-6 bg-grey-400 transition-transform ${
            isOpen ? "translate-y-2 rotate-45" : ""
          }`}
        />
        <span
          className={`block h-0.5 w-6 bg-grey-400 transition-opacity ${
            isOpen ? "opacity-0" : ""
          }`}
        />
        <span
          className={`block h-0.5 w-6 bg-grey-400 transition-transform ${
            isOpen ? "-translate-y-2 -rotate-45" : ""
          }`}
        />
      </button>

      {/* MOBILE OVERLAY */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={closeMenu}
        />
      )}

      {/* MOBILE SLIDE MENU */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-72 bg-white transform transition-transform duration-300 md:hidden ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-end p-4">
          <button
            onClick={closeMenu}
            className="text-grey-400 hover:text-green-500"
          >
            <X className="text-2xl" />
          </button>
        </div>

        <MobileMenuList menu={menu} pathname={pathname} closeMenu={closeMenu} />
      </div>
    </nav>
  );
}
